import { FieldMappingTreeNode } from './fieldMappingTreeBuilder';

/**
 * Field filter options
 */
export type FieldFilter = 'all' | 'required' | 'mapped' | 'unmapped' | 'auto-mapped';

/**
 * Filters tree nodes based on search query with recursive parent-child matching
 * - If node matches: show node + all its children
 * - If any descendant matches: show node + filtered children
 * @param nodes - Tree nodes to filter
 * @param query - Search query string
 * @returns Filtered tree nodes
 */
export const filterTreeBySearch = (
  nodes: FieldMappingTreeNode[],
  query: string
): FieldMappingTreeNode[] => {
  if (!query) {
    return nodes;
  }

  const lowerQuery = query.toLowerCase();

  return nodes.reduce<FieldMappingTreeNode[]>((acc, node) => {
    const nameMatches =
      node.name.toLowerCase().includes(lowerQuery) ||
      node.displayName.toLowerCase().includes(lowerQuery);

    // Recursively filter children
    const filteredChildren = node.children.length > 0
      ? filterTreeBySearch(node.children, query)
      : [];

    // Include node if:
    // 1. The node itself matches (show with all children)
    // 2. Any descendant matches (show with only matching descendants)
    if (nameMatches) {
      // Node matches: include all children
      acc.push({
        ...node,
        children: node.children,
      });
    } else if (filteredChildren.length > 0) {
      // Descendant matches: include parent with filtered children
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

/**
 * Applies field filter (required, mapped, unmapped, auto-mapped) to tree
 * @param nodes - Tree nodes to filter
 * @param filter - Filter type
 * @returns Filtered tree nodes
 */
export const applyFieldFilter = (
  nodes: FieldMappingTreeNode[],
  filter: FieldFilter
): FieldMappingTreeNode[] => {
  if (filter === 'all') {
    return nodes;
  }

  return nodes.reduce<FieldMappingTreeNode[]>((acc, node) => {
    // Apply filter to children first
    const filteredChildren = node.children.length > 0
      ? applyFieldFilter(node.children, filter)
      : [];

    // Determine if this node matches the filter
    let includeNode = false;

    if (node.isLeaf) {
      // For leaf nodes, check the filter condition
      if (filter === 'required') {
        includeNode = node.required;
      } else if (filter === 'mapped') {
        includeNode = !!node.sourceField;
      } else if (filter === 'unmapped') {
        includeNode = !node.sourceField;
      } else if (filter === 'auto-mapped') {
        includeNode = !!(node.isAutoMapped && node.sourceField);
      }
    } else {
      // For parent nodes, include if any child matches
      includeNode = filteredChildren.length > 0;
    }

    if (includeNode) {
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

/**
 * Filters tree data by applying both field filter and search query
 * @param treeData - Original tree data
 * @param filter - Field filter type
 * @param searchQuery - Search query string
 * @returns Filtered tree data
 */
export const filterTreeData = (
  treeData: FieldMappingTreeNode[],
  filter: FieldFilter,
  searchQuery: string
): FieldMappingTreeNode[] => {
  // Apply field filter first (required, mapped, unmapped, auto-mapped)
  let filtered = applyFieldFilter(treeData, filter);

  // Then apply search query
  filtered = filterTreeBySearch(filtered, searchQuery);

  return filtered;
};
